import type { HTMLAttributes, ReactNode } from 'react'
import './feedback.css'

export type Tone = 'neutral' | 'accent' | 'info' | 'success' | 'warning' | 'danger'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  tone?: Tone
  /** Nur Rahmen statt gefuellter Flaeche. */
  outline?: boolean
}

export function Badge({
  tone = 'neutral',
  outline,
  className,
  children,
  ...rest
}: BadgeProps) {
  return (
    <span
      className={['badge', className].filter(Boolean).join(' ')}
      data-tone={tone}
      data-outline={outline || undefined}
      {...rest}
    >
      {children}
    </span>
  )
}

/**
 * Statuspunkt. Farbe allein reicht nicht — wer Rot und Grün nicht unterscheidet,
 * braucht den Text daneben. Deshalb ist `label` Pflicht.
 */
export function StatusDot({ tone = 'neutral', label }: { tone?: Tone; label: string }) {
  return (
    <span className="status-dot" data-tone={tone}>
      <span className="status-dot__dot" aria-hidden="true" />
      {label}
    </span>
  )
}

export interface AlertProps {
  tone?: Tone
  title?: ReactNode
  children?: ReactNode
  actions?: ReactNode
}

export function Alert({ tone = 'info', title, children, actions }: AlertProps) {
  return (
    <div
      className="alert"
      data-tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
    >
      <div className="alert__content">
        {title && <p className="alert__title">{title}</p>}
        {children && <div className="alert__text">{children}</div>}
      </div>
      {actions && <div className="alert__actions">{actions}</div>}
    </div>
  )
}

export function Spinner({ label = 'Wird geladen' }: { label?: string }) {
  return (
    <span className="spinner" role="status" aria-live="polite">
      <span className="spinner__ring" aria-hidden="true" />
      <span className="visually-hidden">{label}</span>
    </span>
  )
}

/** Platzhalter waehrend des Ladens — verhindert Sprünge im Layout. */
export function Skeleton({
  width,
  height = '1em',
  radius = 'var(--radius-sm)',
}: {
  width?: string | number
  height?: string | number
  radius?: string
}) {
  return (
    <span
      className="skeleton"
      style={{ width, height, borderRadius: radius }}
      aria-hidden="true"
    />
  )
}

export interface EmptyStateProps {
  title: string
  description?: ReactNode
  action?: ReactNode
  icon?: ReactNode
}

export function EmptyState({ title, description, action, icon }: EmptyStateProps) {
  return (
    <div className="empty-state">
      {icon && <div className="empty-state__icon">{icon}</div>}
      <p className="empty-state__title">{title}</p>
      {description && <p className="empty-state__text">{description}</p>}
      {action && <div className="empty-state__action">{action}</div>}
    </div>
  )
}
